import {
  createBrowserRouter,
  createHashRouter,
  type RouteObject,
  RouterProvider,
} from 'react-router-dom'
import { MainLayout } from '@/layouts/MainLayout'
import { NotFound } from '@/pages/NotFound'
import { AuthDiscord } from '@/pages/AuthDiscord'
import { AuthDiscordGuild } from '@/pages/AuthDiscordGuild'
import { GiveawayDetailsPage } from '@/pages/GiveawayDetailsPage'
import { Home } from '@/pages/Home'
import { LegacyGiveawayRedirect } from '@/pages/LegacyGiveawayRedirect'
import { MediaPage } from '@/pages/MediaPage'
import { MyGamesPage } from '@/pages/MyGamesPage'
import { MyGiveawaysPage } from '@/pages/MyGiveawaysPage'
import { MyLootPage } from '@/pages/MyLootPage'
import { MyMediaPage } from '@/pages/MyMediaPage'
import { MyProfilePage } from '@/pages/MyProfilePage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { SearchPage } from '@/pages/SearchPage'
import { UserPage } from '@/pages/UserPage'
import { supportsHistoryRouting } from '@/lib/platform'

/*
 * Pages are imported directly rather than lazy-loaded. Each one is 1-6 KB next
 * to a ~400 KB shared bundle, so splitting saved next to nothing, but it made
 * the first visit to every page flash a generic placeholder while its chunk
 * downloaded. Now a page renders at once and shows its own skeleton, in its
 * real shape, while its data loads.
 */

const routes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <MainLayout />,
    children: [
      { index: true, element: <Home /> },

      // The public, shareable address for a giveaway.
      {
        path: 'g/:slug',
        element: <GiveawayDetailsPage />,
      },
      // Links the Quasar app minted; resolved and redirected to /g/:slug.
      {
        path: 'giveaway/:idOrSlug',
        element: <LegacyGiveawayRedirect />,
      },

      // A creator and everything they run.
      { path: 'u/:username', element: <UserPage /> },

      // The signed-in account screens. These were modals in the Quasar app, so
      // they had no address, no back button and no scroll position.
      {
        path: 'my/giveaways',
        element: <MyGiveawaysPage />,
      },
      { path: 'my/loot', element: <MyLootPage /> },
      { path: 'my/media', element: <MyMediaPage /> },
      { path: 'my/games', element: <MyGamesPage /> },
      {
        path: 'my/profile',
        element: <MyProfilePage />,
      },

      // Not under /my: notifications are raised while signed out too (a failed
      // sign-in is one), and those would be unreadable behind the auth guard.
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },

      {
        path: 'auth/discord',
        element: <AuthDiscord />,
      },
      {
        path: 'auth/discord/guild',
        element: <AuthDiscordGuild />,
      },

      // These two existed as components in the Quasar tree but were never
      // routed; they are reachable now (see README).
      { path: 'search', element: <SearchPage /> },
      { path: 'media', element: <MediaPage /> },

      { path: '*', element: <NotFound /> },
    ],
  },
]

/**
 * Electron loads the renderer from file://, where the History API cannot
 * round-trip a path on reload — that build gets hash routing instead.
 */
const router = supportsHistoryRouting() ? createBrowserRouter(routes) : createHashRouter(routes)

export function AppRouter() {
  return <RouterProvider router={router} />
}
